import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  FormLabel,
  Text,
  useToast,
} from '@chakra-ui/react'
import { useGetContractEmailLogs } from '../../hooks/useWizard'
import { formatDateTime } from 'BrokerTsLib/src/Utils/formatPrice'
import { Email, Refresh } from '../../icons'
import { useCallback, useEffect, useRef } from 'react'
import { Loading } from '../../components/Loading'

export const ContractEmailHistory = ({
  paramsId,
  onResend,
}: {
  paramsId: number
  onResend: () => void
}) => {
  const toast = useToast()
  const { data, refetch, isLoading, isFetching } = useGetContractEmailLogs(paramsId)
  const refetchTimeout = useRef<NodeJS.Timeout | null>(null)
  const delay = useRef(500)

  const anyEmailSent = (data?.data.length ?? 0) > 0

  const nextRefetch = useCallback(() => {
    delay.current = Math.min(delay.current * 2, 15000)
    return setTimeout(() => {
      if (!anyEmailSent) {
        refetch().then(() => {
          refetchTimeout.current = nextRefetch()
        })
      }
    }, delay.current)
  }, [anyEmailSent, refetch])

  useEffect(() => {
    refetchTimeout.current = nextRefetch()

    return () => {
      if (refetchTimeout.current) clearTimeout(refetchTimeout.current)
    }
  }, [nextRefetch])

  const manualRefetch = () => {
    refetch().then((res) => {
      if (res.data?.data.length === 0) toast({ title: 'E-mail klientovi dosud nebyl odeslán.' })
      else toast({ title: 'Aktualizováno' })
    })
  }

  return (
    <>
      <FormLabel alignItems={'center'}>
        <Text>Historie odeslaných e-mailů</Text>
        <Button
          ml={2}
          size={'sm'}
          variant={'ghost'}
          onClick={() => manualRefetch()}
          isLoading={isLoading}
        >
          <Refresh />
        </Button>
      </FormLabel>

      {anyEmailSent ? (
        data!.data.map((item, index) =>
          item.success ? (
            <Alert key={index} status="success">
              {formatDateTime(item.dateCreated)} byl odeslán e-mail na pojistníka na adresu{' '}
              {item.to}.
            </Alert>
          ) : (
            <Alert key={index} status="error">
              {formatDateTime(item.dateCreated)} se nepodařilo odeslat e-mail na pojistníka na
              adresu {item.to}.
              <Button
                display={'inline'}
                variant={'link'}
                color={'inherit'}
                ml={1}
                onClick={onResend}
              >
                Odešlete e-mail znovu.
              </Button>
            </Alert>
          ),
        )
      ) : isLoading || delay.current < 4000 ? (
        <Loading />
      ) : (
        <Alert
          status="warning"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          p={6}
        >
          <AlertIcon boxSize="40px" />
          <AlertTitle my={4} fontSize="lg">
            E-mail klientovi dosud nebyl odeslán.
          </AlertTitle>
          <AlertDescription sx={{ display: 'flex', gap: 4 }}>
            <Button
              onClick={() => manualRefetch()}
              colorScheme={'orange'}
              variant={'outline'}
              isLoading={isFetching}
            >
              <Refresh />
              Zkontrolovat stav odeslání
            </Button>
            <Button onClick={onResend} colorScheme={'orange'}>
              <Email /> Odešlete e-mail klientovi manuálně
            </Button>
          </AlertDescription>
        </Alert>
      )}
    </>
  )
}
